import { platformApiFetch } from '@/services/platformApi/client';
import { getUserWhatsAppVerificationStatus as getWhatsAppVerificationStatus } from "@/services/profileService";
import { toast } from "sonner";
import { debugLogger } from '@/utils/debug';
import { getCurrentAuthUser } from '@/services/authSession';

export interface WhatsAppVerificationCodeResult {
  success: boolean;
  message?: string;
  code?: string;
  delivery?: string;
  retryAfter?: number | null;
}

/**
 * Verify a WhatsApp verification code
 * @param providerId The ID of the service provider being verified (optional)
 * @param code The verification code to check
 * @returns Promise<boolean> indicating success
 */
export async function verifyWhatsAppCode(
  providerId: string | null,
  code: string
): Promise<boolean> {
  try {
    if (!code) {
      toast.error("Verification code is required");
      return false;
    }
    
    debugLogger.log("Calling verify-whatsapp with:", { action: "verify", provider_id: providerId, verification_code: code });
    
    const data = await platformApiFetch<any>('/v1/whatsapp/verification', {
      method: 'POST',
      body: JSON.stringify({
      action: "verify",
      provider_id: providerId,
      verification_code: code,
      }),
    });
    
    debugLogger.log("Response from verify-whatsapp:", data);
    
    if (!data || !data.verified) {
      debugLogger.log("Verification failed:", data);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in verifyWhatsAppCode:", error);
    throw error;
  }
}

/**
 * Generate a WhatsApp verification code and send it to the given phone number
 * @param providerId The ID of the service provider being verified (optional)
 * @param phoneNumber The phone number to send the code to
 * @returns Promise with the result of the operation
 */
export async function generateWhatsAppVerificationCode(
  providerId: string | null,
  phoneNumber: string,
  options?: { deliveryMode?: "automated" | "manual"; targetUserId?: string | null }
): Promise<WhatsAppVerificationCodeResult> {
  try {
    if (!phoneNumber) {
      toast.error("Phone number is required");
      return { success: false, message: "Phone number is required" };
    }
    
    // Format phone number to ensure consistency
    // Strip all non-digit characters except leading +
    const formattedPhone = phoneNumber.replace(/^(\+)?(.*?)$/, (_, plus, digits) => {
      return (plus || '') + digits.replace(/\D/g, '');
    });
    
    debugLogger.log("Calling verify-whatsapp with:", { 
      action: "generate", 
      provider_id: providerId, 
      phone_number: formattedPhone,
      delivery_mode: options?.deliveryMode ?? "automated",
      target_user_id: options?.targetUserId ?? null,
    });
    
    const data = await platformApiFetch<any>('/v1/whatsapp/verification', {
      method: 'POST',
      body: JSON.stringify({
      action: "generate",
      provider_id: providerId,
      phone_number: formattedPhone,
      delivery_mode: options?.deliveryMode ?? "automated",
      target_user_id: options?.targetUserId ?? null,
      }),
    });
    
    debugLogger.log("Response from verify-whatsapp for code generation:", data);
    
    if (!data) {
      debugLogger.log("Code generation failed:", data);
      return { 
        success: false, 
        message: "Failed to generate verification code" 
      };
    }

    if (data.success === false) {
      return {
        success: false,
        message: data.error || data.message || "Failed to generate verification code",
        code: data.code || undefined,
        delivery: data.delivery || undefined,
        retryAfter: data.retryAfter ?? null,
      };
    }
    
    // Return the code if present in response
    return { 
      success: true,
      code: data.code || undefined,
      message: data.message || undefined,
      delivery: data.delivery || undefined,
      retryAfter: data.retryAfter ?? null
    };
  } catch (error) {
    console.error("Error in generateWhatsAppVerificationCode:", error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : "Unknown error occurred" 
    };
  }
}

/**
 * Get the WhatsApp verification status for a user
 * @param userId The user ID to check
 * @returns Promise<boolean> indicating if the user's WhatsApp is verified
 */
export async function getUserWhatsAppVerificationStatus(
  userId: string | null
): Promise<boolean> {
  try {
    // If userId is null, get the current user's ID
    if (userId === null) {
      const user = await getCurrentAuthUser();
      if (!user) {
        console.error("User not authenticated");
        return false;
      }
      userId = user.id;
    }
    
    if (!userId) return false;
    
    return await getWhatsAppVerificationStatus(userId);
  } catch (error) {
    console.error("Error in getUserWhatsAppVerificationStatus:", error);
    return false;
  }
}
